// 공고 관리 모달 (SMS 메시지 페이지에서 열림). 위: 수동 추가 / 아래: 구인 중인 자리 표(채널별 스위치).
// 퇴사일이 정해진 직원 자리는 서버가 자동으로 올린다. 데이터: /dashboard/jobopening/*
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import styles from '../../componentsStyle/jobopening.module.css';
import { getFetch, postFetch } from '../../../fetch/fetch';
import InterviewSelect from '../interview/interviewselect';
import JobOpeningLogModal from './jobopeninglogmodal';
import { SWITCHES, branchLabel, sunbaeBranches } from './jobopeningconstants';

const jobsBoxStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 48px)', maxWidth: 1180, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
    bgcolor: '#f2f2f2', borderRadius: '16px', boxShadow: 24, outline: 'none',
    p: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
};

const ListIcon = ({ size = 20 }: { size?: number }) => (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#222222" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10"></path></svg>
);

// "2026-10-31" → "10/31"
const mmdd = (s: string) => s ? `${s.slice(5, 7)}/${s.slice(8, 10)}` : "";

interface Props {
    open: boolean;
    onClose: () => void;
}

const NOTE_SAVE_DELAY = 600;

const JobOpeningModal: React.FC<Props> = ({ open, onClose }) => {

    const [rows, setRows] = useState<any[]>([]);
    const [parts, setParts] = useState<{ id: number, name: string }[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [tableErr, setTableErr] = useState("");
    const [saving, setSaving] = useState<{ [k: string]: boolean }>({});
    const [logRow, setLogRow] = useState<any>(null);

    // 수동 추가 폼
    const [fBranch, setFBranch] = useState("");
    const [fPart, setFPart] = useState("");
    const [fMemo, setFMemo] = useState("");
    const [err, setErr] = useState("");
    const [adding, setAdding] = useState(false);

    // ===== 메모 저장 =====
    // 입력값은 rows에 바로 반영(다시 열어도 최신 값), 서버 저장은 이 모달이 한 곳에서 관리.
    // 저장은 한 줄로 이어서 보내 순서가 뒤집히지 않게 하고, 목록 새로 불러오기 전에 남은 저장을 끝까지 기다린다.
    const notePendingRef = useRef<{ [id: number]: string }>({});
    const noteTimerRef = useRef<any>(null);
    const noteChainRef = useRef<Promise<void>>(Promise.resolve());

    const flushNotes = useCallback((): Promise<void> => {
        if (noteTimerRef.current) { clearTimeout(noteTimerRef.current); noteTimerRef.current = null; }
        const pending = notePendingRef.current;
        notePendingRef.current = {};
        Object.keys(pending).forEach((key) => {
            const id = +key;
            const note = pending[id];
            noteChainRef.current = noteChainRef.current.then(async () => {
                try {
                    const result: any = await postFetch("/dashboard/jobopening/note", { id, note });
                    if (result.message !== "success") throw new Error(result.message);
                } catch (e) {
                    console.log(e);
                    // 그 사이 새로 입력한 값이 없으면 다음 저장 때 다시 시도
                    if (notePendingRef.current[id] === undefined) notePendingRef.current[id] = note;
                    setTableErr("메모 저장에 실패했습니다. 메모 칸을 한 번 더 눌렀다 나오면 다시 저장합니다.");
                }
            });
        });
        return noteChainRef.current;
    }, []);

    const changeNote = (id: number, note: string) => {
        setRows((prev) => prev.map((r) => r.id === id ? { ...r, note } : r));
        notePendingRef.current[id] = note;
        if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
        noteTimerRef.current = setTimeout(flushNotes, NOTE_SAVE_DELAY);
    };

    // 닫을 때(X·바깥 클릭·ESC) 남은 메모 바로 저장
    const handleClose = () => {
        flushNotes();
        onClose();
    };

    // SMS 페이지를 떠날 때도 남은 메모 저장
    useEffect(() => {
        return () => { flushNotes(); };
    }, [flushNotes]);

    const fetchList = useCallback(async () => {
        // 진행 중·대기 중인 메모 저장이 끝난 뒤에 불러와야 방금 쓴 메모가 덮이지 않음
        await flushNotes();
        try {
            const result: any = await getFetch("/dashboard/jobopening/list");
            if (result.message === "success") {
                // 아직 서버에 못 들어간 메모(저장 실패·불러오는 중 입력)는 화면 값을 유지
                const pending = notePendingRef.current;
                setRows(result.data.rows.map((r: any) => pending[r.id] !== undefined ? { ...r, note: pending[r.id] } : r));
                setParts(result.data.parts);
                if (Object.keys(pending).length === 0) setTableErr("");
            } else {
                setTableErr("공고 목록을 불러오지 못했습니다.");
            }
        } catch (e) {
            console.log(e);
            setTableErr("공고 목록을 불러오지 못했습니다.");
        }
        setLoaded(true);
    }, [flushNotes]);

    useEffect(() => {
        if (!open) return;
        setErr("");
        setTableErr("");
        setLogRow(null);
        fetchList();
    }, [open, fetchList]);

    const add = async () => {
        if (!fBranch) { setErr("지점을 고르세요."); return; }
        if (!fPart) { setErr("파트를 고르세요."); return; }
        if (rows.some((r) => r.branchKey === fBranch && String(r.partId) === fPart)) { setErr("이미 구인 목록에 있는 지점·파트입니다."); return; }
        setAdding(true);
        try {
            const result: any = await postFetch("/dashboard/jobopening/create", { branchKey: fBranch, partId: +fPart, memo: fMemo });
            if (result.message === "success") {
                setFMemo("");
                setErr("");
                fetchList();
            } else if (result.message === "DUPLICATE") {
                setErr("이미 구인 목록에 있는 지점·파트입니다.");
                fetchList();
            } else {
                setErr("추가에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            setErr("추가에 실패했습니다.");
        } finally {
            setAdding(false);
        }
    };

    // 화면 먼저 바꾸고 서버 저장, 실패하면 되돌림 (팝업 없이 표 위 문구로)
    const toggle = async (row: any, key: string) => {
        const savingKey = `${row.id}:${key}`;
        if (saving[savingKey]) return;
        const nextOn = +row[key] ? 0 : 1;
        const setValue = (v: number) => setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, [key]: v } : r));
        setValue(nextOn);
        setSaving((prev) => ({ ...prev, [savingKey]: true }));
        try {
            const result: any = await postFetch("/dashboard/jobopening/toggle", { id: row.id, key, on: nextOn });
            if (result.message !== "success") throw new Error(result.message);
            setTableErr("");
        } catch (e) {
            console.log(e);
            setValue(nextOn ? 0 : 1);
            setTableErr(`${branchLabel(row.branchKey)} ${row.partName} 스위치 저장에 실패했습니다. 다시 눌러 주세요.`);
        } finally {
            setSaving((prev) => { const n = { ...prev }; delete n[savingKey]; return n; });
        }
    };

    const remove = async (row: any) => {
        if (!window.confirm(`${branchLabel(row.branchKey)} ${row.partName} 자리를 구인 목록에서 삭제할까요?`)) return;
        try {
            const result: any = await postFetch("/dashboard/jobopening/delete", { id: row.id });
            if (result.message === "success") {
                setRows((prev) => prev.filter((r) => r.id !== row.id));
                setTableErr("");
            } else {
                setTableErr("삭제에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            setTableErr("삭제에 실패했습니다.");
        }
    };

    return (
        <Modal open={open} onClose={handleClose} sx={{ zIndex: 600 }}>
            <Box sx={jobsBoxStyle} className={styles.wrap} role="dialog" aria-label="공고 관리">
                <div className={styles.headerRow}>
                    <div className={styles.titleRow}>
                        <ListIcon />
                        <h2 className={styles.title}>공고 관리</h2>
                        <div className={styles.titleHint}>직원관리에서 퇴사일이 정해지면 자동으로 올라옵니다. AI가 이 스위치를 보고 공고·광고를 올립니다.</div>
                    </div>
                    <button className={styles.circleBtn} aria-label="닫기" onClick={handleClose}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                    </button>
                </div>

                {/* 수동 추가 */}
                <section className={styles.card}>
                    <div className={styles.cardTitle}>수동 추가</div>
                    <div className={styles.formRow}>
                        <div className={styles.field}>지점
                            <InterviewSelect value={fBranch} placeholder="지점 선택" options={sunbaeBranches}
                                onChange={(v) => { setFBranch(v); setErr(""); }} />
                        </div>
                        <div className={styles.field}>파트
                            <div style={{ minWidth: 240 }}>
                                <InterviewSelect value={fPart} placeholder="파트 선택" fullWidth
                                    options={parts.map((p) => ({ value: String(p.id), label: p.name }))}
                                    onChange={(v) => { setFPart(v); setErr(""); }} />
                            </div>
                        </div>
                        <label className={styles.fieldGrow}>메모 (선택)
                            <input type="text" className={styles.input} value={fMemo} placeholder="증원, 교체 등" onChange={(e) => setFMemo(e.target.value)} />
                        </label>
                        <button className={styles.addBtn} onClick={add} disabled={adding}>추가</button>
                    </div>
                    {err ? <div className={styles.err}>{err}</div> : null}
                </section>

                {/* 구인 중인 자리 */}
                <section className={styles.card}>
                    <div className={styles.tableHead}>
                        <div className={styles.cardTitle}>구인 중인 자리 <span className={styles.count}>{rows.length}건</span></div>
                        <div className={styles.legend}>
                            <span className={styles.legendItem}><span className={styles.swatchAuto}></span>퇴사 예정으로 자동 생성</span>
                            <span className={styles.legendItem}><span className={styles.swatchManual}></span>수동 추가</span>
                            <span>스위치는 누르는 즉시 저장됩니다. 시계 아이콘으로 변경 기록을 봅니다.</span>
                        </div>
                    </div>
                    {tableErr ? <div className={styles.err}>{tableErr}</div> : null}
                    <div className={styles.tableScroll}>
                        <div className={styles.table}>
                            <div className={styles.rowHead}>
                                <div>지점</div>
                                <div>파트</div>
                                <div>사유</div>
                                <div>메모</div>
                                {SWITCHES.map((s) => <div key={s.key} className={styles.center}>{s.label}</div>)}
                                <div></div>
                            </div>
                            {rows.map((r) => (
                                <div key={r.id} className={styles.row}>
                                    <div className={styles.cellBranch}>{branchLabel(r.branchKey)}</div>
                                    <div className={styles.cellPart}>{r.partName}</div>
                                    <div className={styles.cellReason}>
                                        {r.source === "auto" ? (
                                            <div className={styles.badgeAuto}>
                                                <span className={styles.badgeTitle}>{r.leaveDate ? `퇴사 예정 ${mmdd(r.leaveDate)}` : "퇴사 예정 취소됨"}</span>
                                                <span>{r.employeeName ? `${r.employeeName} 퇴사` : ""}{r.memo ? ` · ${r.memo}` : ""}</span>
                                            </div>
                                        ) : (
                                            <div className={styles.badgeManual}>
                                                <span className={styles.badgeTitle}>수동 추가</span>
                                                <span>{r.memo || "사유 없음"}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.cellNote}>
                                        <input className={styles.noteInput} value={r.note || ""} maxLength={500} placeholder="메모"
                                            onChange={(e) => changeNote(r.id, e.target.value)} onBlur={() => { flushNotes(); }} />
                                    </div>
                                    {SWITCHES.map((s) => {
                                        const on = !!+r[s.key];
                                        return (
                                            <div key={s.key} className={styles.cellSwitch}>
                                                <button className={`${styles.toggle} ${on ? styles.toggleOn : ""}`} aria-label={`${branchLabel(r.branchKey)} ${r.partName} ${s.label}`}
                                                    aria-pressed={on} disabled={!!saving[`${r.id}:${s.key}`]} onClick={() => toggle(r, s.key)}>
                                                    <span className={styles.knob}></span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <div className={styles.cellActions}>
                                        <button className={styles.iconBtn} aria-label="변경 기록" title="스위치 변경 기록" onClick={() => setLogRow(r)}>
                                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3346A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>
                                        </button>
                                        <button className={styles.iconBtn} aria-label="삭제" title="구인 목록에서 삭제" onClick={() => remove(r)}>
                                            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a32218" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"></path></svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {loaded && rows.length === 0 ? <div className={styles.empty}>구인 중인 자리가 없습니다.</div> : null}
                        </div>
                    </div>
                </section>

                {logRow && (
                    <JobOpeningLogModal row={logRow} branchLabel={branchLabel(logRow.branchKey)} onClose={() => setLogRow(null)} />
                )}
            </Box>
        </Modal>
    );
};

export default JobOpeningModal;
